import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Home, Blocks, ShieldCheck, Clock, Ruler } from "lucide-react";
import api from "@/lib/api";
import { Logo, Loader } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";

const IMAGES = {
  "loft-conversion": "/loft-conversion.webp",
  "property-extension": "/property-extension.webp",
};
const ICONS = { "loft-conversion": Home, "property-extension": Blocks };

export default function Landing() {
  const [categories, setCategories] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data.categories)).catch(() => setCategories([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#090A0C] text-slate-50">
      <header className="sticky top-0 z-40 border-b border-slate-800/80 bg-[#090A0C]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <Logo />
          <div className="flex items-center gap-3">
            {user ? (
              <Link to={user.role === "admin" ? "/admin" : "/dashboard"}
                className="rounded-lg border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 hover:border-amber-500/50"
                data-testid="nav-dashboard-btn">
                {user.role === "admin" ? "Admin" : "My Dashboard"}
              </Link>
            ) : (
              <Link to="/login" className="text-sm font-semibold text-slate-300 hover:text-amber-400"
                data-testid="nav-signin-link">Sign in</Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden evo-blueprint-grid">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24">
          <div className="evo-fade-up max-w-2xl">
            <img src="/evostruct-logo.png" alt="EvoStruct" className="h-32 w-auto object-contain sm:h-44 lg:h-52" />
            <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Get Your Project<br /><span className="text-amber-500">Estimate</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
              Answer a few questions about your project and receive an estimated quotation based on your requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Project cards */}
      <section className="mx-auto max-w-6xl px-4 pb-12 sm:px-6">
        {categories === null ? <Loader label="Loading projects" /> : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
            {categories.map((cat, i) => {
              return (
                <div key={cat.id}
                  className="evo-fade-up group relative overflow-hidden rounded-2xl border border-slate-800 bg-gradient-to-b from-slate-900 to-[#121419] shadow-xl transition-all duration-300 hover:border-amber-500/50"
                  style={{ animationDelay: `${i * 0.08}s` }}
                  data-testid={`project-card-${cat.slug}`}>
                  <div className="relative h-44 overflow-hidden sm:h-52">
                    {IMAGES[cat.id] ? (
                      <img src={IMAGES[cat.id]} alt={cat.name}
                        className="h-full w-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div className="h-full w-full bg-gradient-to-br from-slate-700 via-slate-900 to-[#121419] evo-blueprint-grid" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121419] via-[#121419]/40 to-transparent" />
                  </div>
                  <div className="p-6 sm:p-7">
                    <h3 className="font-heading text-2xl font-bold">{cat.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{cat.description}</p>
                    <button onClick={() => navigate(`/quote/${cat.id}`)}
                      className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-600 to-amber-500 px-6 py-3.5 font-bold text-slate-950 shadow-lg shadow-amber-500/20 transition-all hover:from-amber-500 hover:to-amber-400 active:scale-95"
                      data-testid={`get-quote-btn-${cat.slug}`}>
                      Get {cat.name} Quote <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-8 text-center text-sm text-slate-400">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-amber-400 hover:underline" data-testid="landing-signin-link">
            Sign in
          </Link>
        </p>
      </section>

      {/* Trust strip */}
      <section className="border-t border-slate-800/60">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-4 py-12 sm:grid-cols-3 sm:px-6">
          {[
            [ShieldCheck, "Transparent estimates", "Clear, itemised breakdowns with no hidden surprises."],
            [Clock, "In minutes, not weeks", "Answer a few simple questions and get an indicative figure instantly."],
            [Blocks, "Design to build", "One team for design, planning, structural and construction."],
          ].map(([Icon, title, body]) => (
            <div key={title} className="rounded-xl border border-slate-800/80 bg-[#151820] p-6">
              <Icon className="h-6 w-6 text-amber-500" />
              <h4 className="mt-4 font-heading text-lg font-semibold">{title}</h4>
              <p className="mt-1.5 text-sm text-slate-400">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-800/60 py-8 text-center text-xs text-slate-500">
        <p>EvoStruct Ltd — Loft Conversions, Extensions, Design &amp; Build</p>
        <p className="mt-1">Estimated quotations are indicative and subject to survey and confirmation of scope.</p>
      </footer>
    </div>
  );
}
